import i18next from 'i18next';

/**
 * Strips the country suffix from a locale code.
 * @example toLangCode('nl-BE') // => 'nl'
 * @example toLangCode('nl')    // => 'nl'
 */
export function toLangCode(code: string): string {
  if (!code) return code;
  return code.split('-')[0].split('_')[0];
}

/**
 * Ensures the URL contains the correct client type (B2B/B2C) and short
 * language segment, e.g. /B2C/nl/products.
 */
export function handleRedirects(fullPath: string, language: string, isB2B: boolean) {
  const urlSegments = fullPath.split('/').filter(segment => segment);
  const clientType = urlSegments[0]; // B2B or B2C

  // Redirect if the path doesn't include B2B or B2C
  if (clientType !== 'B2B' && clientType !== 'B2C') {
    const newPath = isB2B ? '/B2B' : '/B2C';
    window.location.replace(newPath);
    return;
  }

  // Never redirect OIDC callback paths — they carry query params (code, state)
  // that the OIDC library must read before any navigation occurs.
  if (fullPath.includes('/authentication/callback') || fullPath.includes('/authentication/silent_callback')) {
    return;
  }

  // If no language is provided yet, bail out
  if (!language) return;

  const shortLang = toLangCode(language);

  // Determine whether the second segment is already a language code (e.g. 'nl', 'fr', 'en-BE')
  const secondSegment = urlSegments[1];
  const isLanguageSegment = !!secondSegment && /^[a-z]{2}(-[A-Z]{2})?$/.test(secondSegment);

  // Preserve any query string / hash so we don't lose state during redirect
  const search = window.location.search;
  const hash = window.location.hash;
  const suffix = search + hash;

  if (isLanguageSegment) {
    // Second segment is a language code — nothing to do if it already matches
    if (secondSegment === shortLang) return;

    // Replace the existing language segment and redirect
    const remainingSegments = urlSegments.slice(2);
    const newSegments = [clientType, shortLang, ...remainingSegments];
    const newPath = `/${newSegments.join('/')}${suffix}`;

    if (newPath !== fullPath) {
      i18next.changeLanguage(language);
      window.location.replace(newPath);
    }
  } else {
    // No language segment present — insert it after the clientType,
    // preserving the existing path (e.g. /B2C/products/movie-boxes → /B2C/nl/products/movie-boxes)
    const remainingSegments = urlSegments.slice(1);
    const newSegments = [clientType, shortLang, ...remainingSegments];
    const newPath = `/${newSegments.join('/')}${suffix}`;

    if (newPath !== fullPath) {
      i18next.changeLanguage(language);
      window.location.replace(newPath);
    }
  }
}

/**
 * Returns the language-aware basename for BrowserRouter.
 * Uses only the short language code (no country suffix).
 *
 * @example getLangBasename('/B2C', 'nl-BE') // => '/B2C/nl'
 */
export function getLangBasename(businessPathName: string, language: string): string {
  if (!language) return businessPathName;
  const base = businessPathName?.replace(/\/$/, '') ?? '';
  return `${base}/${toLangCode(language)}`;
}
